import { computed, shallowReactive, watch } from 'vue';

import { useCatalog } from './useCatalog';

const metadataCache = shallowReactive(new Map());
const regionLoading = new Map();

export function useRegionMetadata(region) {
    const { getRegionMetadata } = useCatalog();
    let loadingPromise = null;

    const regionMetadata = computed(() => {
        if (!region.value) {
            return null;
        }

        return metadataCache.get(region.value) ?? null;
    });

    async function fetchRegionMetadata(regionId) {
        if (metadataCache.has(regionId)) {
            return metadataCache.get(regionId);
        }

        if (regionLoading.has(regionId)) {
            return regionLoading.get(regionId);
        }

        const request = getRegionMetadata(regionId)
            .then((value) => {
                metadataCache.set(regionId, value);
                return value;
            })
            .finally(() => {
                regionLoading.delete(regionId);
            });

        regionLoading.set(regionId, request);
        return request;
    }

    async function load() {
        if (loadingPromise) {
            return loadingPromise;
        }
        if (!region.value) {
            return null;
        }

        const capturedId = region.value;
        loadingPromise = fetchRegionMetadata(capturedId)
            .then(() => {
                if (region.value !== capturedId) {
                    return;
                }
            })
            .catch((error) => {
                console.error(`Failed to load region '${capturedId}' metadata`, error);
            });

        return loadingPromise;
    }

    const intellectualPropertyText = computed(() => {
        return regionMetadata.value?.STR_IP_TEXT ?? null;
    });

    const regulationsVersion = computed(() => {
        return regionMetadata.value?.regulations_version ?? null;
    });

    const documentsList = computed(() => {
        return regionMetadata.value?.DOCUMENTS_JSON ?? [];
    });

    const documentsListFrench = computed(() => {
        return regionMetadata.value?.DOCUMENTS_JSON_FR ?? [];
    });

    const noticesList = computed(() => {
        return regionMetadata.value?.NOTICES ?? [];
    });

    const cwdZoneList = computed(() => {
        return noticesList.value?.length ? noticesList.value.filter((x) => x.type === 'CWD').flatMap((x) => x.zones) : [];
    });

    watch(
        region,
        async (newRegion, oldRegion) => {
            if (newRegion !== oldRegion) {
                loadingPromise = null;

                if (newRegion) {
                    await load();
                }
            }
        },
        { immediate: true },
    );

    return {
        regionMetadata,
        regulationsVersion,

        intellectualPropertyText,

        documentsList,
        documentsListFrench,
        noticesList,
        cwdZoneList,
    };
}
